import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import { collection, addDoc, doc, getDocs, updateDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import Modal from 'react-modal';
import Logo from '../images/school_logo.png';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';

Modal.setAppElement('#root');

const Subjects = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [update, setUpdate] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [newSubject, setNewSubject] = useState({ name: '', code: '' });
  const [editingSubject, setEditingSubject] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentSubject, setCurrentSubject] = useState(null);
  const [isFetchingSubjects, setIsFetchingSubjects] = useState(true);
  const [searchSubject, setSearchSubject] = useState('');

  const subjectsCollectionRef = collection(db, "subjects");

  useEffect(() => {
    fetchSubjects();
  }, []);

  const fetchSubjects = async () => {
    setIsFetchingSubjects(true);
    try {
      const data = await getDocs(subjectsCollectionRef);
      setSubjects(data.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
    } catch (error) {
      setError("Failed to fetch subjects");
      setShowModal(true);
    } finally {
      setIsFetchingSubjects(false);
    }
  };

  const filteredSubjects = subjects.filter(subject =>
    subject.name.toLowerCase().includes(searchSubject.toLowerCase()) ||
    subject.code.toLowerCase().includes(searchSubject.toLowerCase())
  );  

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewSubject({ ...newSubject, [name]: value });
  };

  const handleAddSubject = async () => {
    setError("");
    setSuccessMessage("") ;

    const requiredFields = {
      name: "Subject name",
      code: "Subject code",
    };
    for (const [field, displayName] of Object.entries(requiredFields)) {
      const value = field === "name" ? newSubject[field].trim() : newSubject[field];
      if (!value) {
        setError(`${displayName} cannot be empty`);
        setShowModal(true);
        return;
      }
    }  
    setUpdate(true);
    setError("");
    setSuccessMessage("");
    try {
      await addDoc(subjectsCollectionRef, {
        ...newSubject,
        createdAt: Timestamp.now()
      });
      setSuccessMessage("Subject added successfully");
      fetchSubjects();
      setNewSubject({ name: '', code: ''});
      setIsAddModalOpen(false);
    } catch (error) {
      setError("Failed to add subject");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleUpdateSubject = async () => {
    setUpdate(true);
    try {
      const subjectDoc = doc(db, "subjects", editingSubject.id);
      await updateDoc(subjectDoc, newSubject);
      setSuccessMessage("Subject updated successfully");
      fetchSubjects();
      setEditingSubject(null);
      setNewSubject({ name: '', code: '' });
      setIsEditModalOpen(false);
    } catch (error) {
      setError("Failed to update subject");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleEditSubject = (subjectToEdit) => {
    setEditingSubject(subjectToEdit);
    setNewSubject({ name: subjectToEdit.name, code: subjectToEdit.code });
    setIsEditModalOpen(true);
  };

  const handleDeleteSubject = async (id) => {
    setUpdate(true);
    try {
      const subjectDoc = doc(db, "subjects", id);
      await deleteDoc(subjectDoc);
      setSuccessMessage("Subject deleted successfully");
      fetchSubjects();
    } catch (error) {
      setError("Failed to delete subject");
    } finally {
      setUpdate(false);
      setIsDeleteModalOpen(false);
      setShowModal(true);
    }
  };

  const openDeleteModal = (subject) => {
    setCurrentSubject(subject);
    setIsDeleteModalOpen(true);
  };

  if (loading) {
    return <Loader text={"LOADING"} />;
  }

  if (!userData) {
    return <Loader text={"READING"} />;
  }

  const allowedRoles = ['Admin', 'Cashier'];

  return (
    <>
      {update && <Loader text={"PROCESSING"} />}
      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <div className="column align-center justify-center">
              <img src={Logo} className="middle-logo" alt="logo" />
            </div>
            <Link to="#" className="home-link" onClick={() => fetchSubjects()}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>
          <div className="login-page-items column align-center scroll-fixed">
            <h2 className="manage-page-heading caps size4">Manage Subjects</h2>
            <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>Add Subject</button>
            <input
              type="text"
              placeholder="Search by name or code"
              className="input-field1"
              value={searchSubject}
              onChange={(e) => setSearchSubject(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {isFetchingSubjects ? (
                  <p className="loading-text">Loading subjects...</p>
                ) : subjects.length === 0 ? (
                  <p className="loading-text">No subjects available</p>
                ) : (
                  filteredSubjects.map((subjectItem) => (
                    <div key={subjectItem.id} className="class-item">
                      <p>{subjectItem.name} ({subjectItem.code})</p>
                      <div className="row align-center justify-center gap-15">
                        <button className="list-btn" onClick={() => handleEditSubject(subjectItem)}><i className="fas fa-edit"></i></button>
                        {/* <button className="list-btn" onClick={() => openDeleteModal(subjectItem)}><i className="fas fa-trash-alt"></i></button> */}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
          </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}
      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      {/* Add Subject Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onRequestClose={() => setIsAddModalOpen(false)}
        contentLabel="Add Subject"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Add Subject</h2>
        <form className="subject-form">
          <input
            type="text"
            name="name"
            className="input-field"
            placeholder="Subject Name"
            value={newSubject.name}
            onChange={handleInputChange}
          />
          <input
            type="text"
            name="code"
            className="input-field"
            placeholder="Subject Code"
            value={newSubject.code}
            onChange={handleInputChange}
          />
          <div className="row align-center gap-1">
            <button className="login-button" type="button" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
            <button className="login-button" type="button" onClick={handleAddSubject}>Add Subject</button>
          </div>
        </form>
      </Modal>

      {/* Edit Subject Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onRequestClose={() => {
          setIsEditModalOpen(false);
          setNewSubject({ name: '', code: '' }); // Reset newSubject
          setEditingSubject(null); // Reset editingSubject
        }}
        contentLabel="Edit Subject"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Edit Subject</h2>
        <form className="subject-form">
          <input
            type="text"
            name="name"
            className="input-field"
            placeholder="Subject Name"
            value={newSubject.name}
            onChange={handleInputChange}
          />
          <input
            type="text"
            name="code"
            className="input-field"
            placeholder="Subject Code"
            value={newSubject.code}
            onChange={handleInputChange}
          />          
          <div className="row align-center justify-center gap-1">            
            <button className="login-button" type="button" onClick={() => {
                setIsEditModalOpen(false);
                setNewSubject({ name: '', code: '' }); // Reset newSubject
                setEditingSubject(null); // Reset editingSubject
              }}>Cancel</button>
            <button className="login-button" type="button" onClick={handleUpdateSubject}>Update Subject</button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onRequestClose={() => setIsDeleteModalOpen(false)}
        contentLabel="Delete Subject"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Delete Subject</h2>
        {currentSubject && (
          <div>
            <p className="male-female-stat">Are you sure you want to delete <strong>{currentSubject.name}</strong>?</p>
            <div className="row align-center gap-1">
              <button className="login-button" type="button" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
              <button className="login-button" type="button" onClick={() => handleDeleteSubject(currentSubject.id)}>Delete</button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default Subjects;